import { AttachmentBase, ClientPopulated } from '@api/READ_ONLY/client_api/Api';
import { VisitPopulated, VisitStatusEnum } from '@api/READ_ONLY/visits_api/Api';
import {
  ATTACHMENT_BUCKET_LABEL_KEYS,
  ATTACHMENT_BUCKET_ORDER,
  type AttachmentBucket,
  attachmentBucketOf,
} from '@app/features/attachments/attachmentKind';
import dayjs from '@app/utils/dayjs-setup';

/**
 * Everything the client card shows is derived from one visits fetch plus the client.
 * These are those derivations, kept out of the components so each one can be tested
 * against the awkward cases the real data has: a visit with no caregiver, a vital
 * measured three times in ten weeks, a client whose doctor's spell has ended.
 *
 * Notes are not among them. They live in `notes`, are fetched by the notes panels on
 * their own, and are not a field of the visit any more.
 */

/** A visit as the card reads it — the listing response, named for what reads it. */
export type CardVisit = VisitPopulated;

/** An attachment, from either the client or a visit; the two shapes are the same. */
export type CardAttachment = AttachmentBase;

/**
 * The span every panel on the card is derived from, as ISO instants.
 *
 * Carried down to the panels rather than left in the hook: a panel that states a
 * count — "3 measurements", "99 visits" — has to be able to say what it counted
 * over, or the reader takes it for the client's whole history.
 */
export type CardWindow = { from: string; to: string };

/**
 * How far back and forward the card's single visits fetch reaches.
 *
 * Here rather than beside the query, because the panels state the span they are
 * counting over and must name the same numbers the fetch used.
 */
export const CARD_WINDOW_DAYS_BACK = 120;
export const CARD_WINDOW_DAYS_FORWARD = 60;

/* ================= Visit facts ================= */

/** Visit statuses that still lie ahead: the ones "next visit" may point at. */
const UPCOMING_STATUSES: VisitStatusEnum[] = [VisitStatusEnum.SCHEDULED, VisitStatusEnum.IN_PROGRESS];

/** Existing keys, so a status reads the same here as it does on the visit form. */
export const VISIT_STATUS_LABEL_KEYS: Record<VisitStatusEnum, string> = {
  [VisitStatusEnum.SCHEDULED]: 'visits-status-scheduled',
  [VisitStatusEnum.IN_PROGRESS]: 'visits-status-in-progress',
  [VisitStatusEnum.COMPLETED]: 'visits-status-completed',
  [VisitStatusEnum.CANCELLED]: 'visits-status-cancelled',
};

export const visitStatusLabelKey = (visit: CardVisit): string =>
  visit.status ? VISIT_STATUS_LABEL_KEYS[visit.status] : 'visits-status-not-set';

/** Every status, in the order a visit moves through them. */
export const VISIT_STATUSES: VisitStatusEnum[] = [
  VisitStatusEnum.SCHEDULED,
  VisitStatusEnum.IN_PROGRESS,
  VisitStatusEnum.COMPLETED,
  VisitStatusEnum.CANCELLED,
];

/**
 * Chip colours for the four statuses. The filter reads the same way as the tags it
 * filters on - green done, blue running, slate ahead, grey called off - but in hex,
 * because a chip is coloured from values and an antd tag from a palette name.
 */
export const VISIT_STATUS_CHIP_STYLE: Record<VisitStatusEnum, { fg: string; bg: string; bd: string }> = {
  [VisitStatusEnum.SCHEDULED]: { fg: '#475569', bg: '#f8fafc', bd: '#e2e8f0' },
  [VisitStatusEnum.IN_PROGRESS]: { fg: '#1d4ed8', bg: '#eff6ff', bd: '#bfdbfe' },
  [VisitStatusEnum.COMPLETED]: { fg: '#15803d', bg: '#f0fdf4', bd: '#bbf7d0' },
  [VisitStatusEnum.CANCELLED]: { fg: '#64748b', bg: '#f1f5f9', bd: '#cbd5e1' },
};

/**
 * The visits whose status is among `statuses`, or all of them when none is chosen.
 *
 * An empty choice means "no filter", not "nothing": a reader who unticks their last
 * status is taking the filter off, and an empty timeline would read as a client with
 * no care rather than as a control they left in a corner.
 */
export const filterVisitsByStatus = (visits: CardVisit[], statuses: VisitStatusEnum[]): CardVisit[] =>
  statuses.length === 0 ? visits : visits.filter((visit) => !!visit.status && statuses.includes(visit.status));

/** The visit's length in minutes, from its own start and end. */
export const visitDurationMinutes = (visit: CardVisit): number =>
  Math.max(0, dayjs(visit.endTime).diff(dayjs(visit.startTime), 'minute'));

/**
 * Minutes the caregiver arrived after the scheduled start, or `null`.
 *
 * `null` covers two different things the card renders identically — no check-in
 * recorded, and a caregiver who was on time or early — because neither is worth a
 * row of its own. Only lateness is. Computed from the two instants, so a visit
 * spanning a DST change is still measured correctly.
 */
export const visitLateMinutes = (visit: CardVisit): number | null => {
  if (!visit.checkInAt) return null;
  const late = dayjs(visit.checkInAt).diff(dayjs(visit.startTime), 'minute');
  return late > 0 ? late : null;
};

/**
 * Every caregiver on the visit with their id, in the order the API returned them.
 *
 * The id comes along because two caregivers can share a name, and anything that
 * renders one per row needs a key that tells them apart — the same reason
 * {@link buildCareTeam} is keyed by id.
 */
export const visitCaregivers = (visit: CardVisit): Array<{ caregiverId: number; name: string }> =>
  (visit.caregivers ?? [])
    .map((caregiver) => ({
      caregiverId: caregiver.caregiverId,
      name: `${caregiver.firstName ?? ''} ${caregiver.lastName ?? ''}`.trim(),
    }))
    .filter((caregiver) => caregiver.name.length > 0);

/** Their names alone, for the places that only write them out. */
export const visitCaregiverNames = (visit: CardVisit): string[] =>
  visitCaregivers(visit).map((caregiver) => caregiver.name);

/** The visit's services, each with the minutes its catalogue entry estimates. */
export const visitServiceLines = (visit: CardVisit): Array<{ id: number; name: string; minutes: number }> =>
  (visit.services ?? []).map((line) => ({
    // The line's own id, not its name: a visit can carry the same service twice.
    id: line.id,
    name: line.service.name,
    minutes: line.service.estimatedTimeMinutes ?? 0,
  }));

/**
 * The next visit that has not happened yet, or `null`.
 *
 * Compared as instants against `now`, and cancelled visits are never candidates —
 * pointing the header's first figure at a visit nobody will make is worse than
 * showing none.
 */
export const nextVisit = (visits: CardVisit[], now: Date = new Date()): CardVisit | null => {
  const reference = dayjs(now);
  const upcoming = visits
    .filter((visit) => visit.status && UPCOMING_STATUSES.includes(visit.status))
    .filter((visit) => !dayjs(visit.endTime).isBefore(reference))
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

  return upcoming[0] ?? null;
};

/** How many days back the header's visit counts look. */
export const RECENT_WINDOW_DAYS = 30;

export type RecentVisitCounts = { completed: number; cancelled: number };

/**
 * Completed and cancelled visits in the last {@link RECENT_WINDOW_DAYS} days.
 *
 * A rolling window rather than the calendar month on purpose: on the 2nd of a month
 * the calendar figure is one visit and says nothing, while the reader's question —
 * how much care has this client actually had lately — has not changed.
 */
export const recentVisitCounts = (visits: CardVisit[], now: Date = new Date()): RecentVisitCounts => {
  const from = dayjs(now).subtract(RECENT_WINDOW_DAYS, 'day');
  const reference = dayjs(now);

  const inWindow = visits.filter((visit) => {
    const start = dayjs(visit.startTime);
    return !start.isBefore(from) && !start.isAfter(reference);
  });

  return {
    completed: inWindow.filter((visit) => visit.status === VisitStatusEnum.COMPLETED).length,
    cancelled: inWindow.filter((visit) => visit.status === VisitStatusEnum.CANCELLED).length,
  };
};

/* ================= Care team ================= */

export type TeamMember = {
  caregiverId: number;
  name: string;
  /**
   * Visits this caregiver has COMPLETED for the client, inside the fetched window.
   *
   * Can be 0 for someone who has plainly been: a visit whose time has passed but
   * which nobody marked completed counts towards {@link last}, never towards this.
   * Anything that shows the number must say what it is, or stay silent at 0.
   */
  completed: number;
  /** Their soonest visit still ahead of them, if they have one. */
  next: CardVisit | null;
  /** Their most recent visit already behind them, whatever its status. */
  last: CardVisit | null;
};

export type CareTeam = {
  /** Whoever comes next, falling back to whoever came last. */
  current: TeamMember | null;
  /** Everyone else with a visit still ahead of them, soonest first. */
  upcoming: TeamMember[];
  /** Everyone whose visits are all behind them, most recent first. */
  previous: TeamMember[];
};

/**
 * Who is looking after this client, derived from the visits themselves.
 *
 * There is no "assigned team" anywhere in the data — a caregiver's connection to a
 * client IS the set of visits they were staffed on. Cancelled visits are ignored:
 * being scheduled for a visit that was called off never made anyone this client's
 * caregiver. A visit with several caregivers counts for each of them.
 *
 * Three groups, because having been and being booked are different facts and the
 * coordinator is usually asking the second. Everyone staffed on a visit in the
 * window lands in exactly one of them: `previous` is only for caregivers with
 * nothing ahead of them, so a name never appears twice.
 */
export const buildCareTeam = (visits: CardVisit[], now: Date = new Date()): CareTeam => {
  // Keyed by id, not by name. Two caregivers called Μαρία Παπαδοπούλου would
  // otherwise collapse into one row whose visit count is the sum of theirs, and the
  // "current" tag could land on the wrong one while the person actually coming next
  // never appears at all.
  const byId = new Map<number, TeamMember>();
  const reference = dayjs(now);

  const relevant = visits.filter((visit) => visit.status !== VisitStatusEnum.CANCELLED);

  for (const visit of relevant) {
    for (const caregiver of visit.caregivers ?? []) {
      const name = `${caregiver.firstName ?? ''} ${caregiver.lastName ?? ''}`.trim();
      if (!name) continue;

      const member = byId.get(caregiver.caregiverId) ?? {
        caregiverId: caregiver.caregiverId,
        name,
        completed: 0,
        next: null,
        last: null,
      };

      // Behind or ahead is a question about the clock, not about the status. Most of
      // this client's history is visits nobody ever marked COMPLETED — the caregiver
      // came, the app was never tapped — and reading "has been here" off the status
      // dropped those people out of every group while the timeline still showed them.
      if (visit.status === VisitStatusEnum.COMPLETED) member.completed += 1;

      if (dayjs(visit.endTime).isBefore(reference)) {
        if (!member.last || dayjs(visit.startTime).isAfter(dayjs(member.last.startTime))) {
          member.last = visit;
        }
      } else if (!member.next || dayjs(visit.startTime).isBefore(dayjs(member.next.startTime))) {
        member.next = visit;
      }

      byId.set(caregiver.caregiverId, member);
    }
  }

  const members = [...byId.values()];

  const booked = members
    .filter((member) => member.next)
    .sort((a, b) => dayjs(a.next!.startTime).valueOf() - dayjs(b.next!.startTime).valueOf());

  const seen = members
    .filter((member) => member.last)
    .sort((a, b) => dayjs(b.last!.startTime).valueOf() - dayjs(a.last!.startTime).valueOf());

  // An id is read off the next visit only when it actually carries a caregiver — an
  // unstaffed visit is a first-class state here. Then whoever is booked soonest,
  // because someone coming on Thursday is more current than someone last seen three
  // weeks ago; and only if nobody is booked at all does this fall back to who came
  // last, rather than reporting that the client has nobody.
  const staffingNextVisit = nextVisit(visits, now)?.caregivers?.[0]?.caregiverId;
  const current =
    (staffingNextVisit === undefined ? undefined : byId.get(staffingNextVisit)) ?? booked[0] ?? seen[0] ?? null;

  const upcoming = booked.filter((member) => member.caregiverId !== current?.caregiverId);
  const bookedIds = new Set(upcoming.map((member) => member.caregiverId));

  const previous = seen.filter(
    (member) => member.caregiverId !== current?.caregiverId && !bookedIds.has(member.caregiverId)
  );

  return { current, upcoming, previous };
};

/* ================= Vitals ================= */

/** The vitals a visit can record, with the label and unit the visit form uses. */
export type VitalKey =
  | 'bloodPressureSystolic'
  | 'bloodPressureDiastolic'
  | 'heartRate'
  | 'oxygenSaturation'
  | 'respiratoryRate'
  | 'temperature'
  | 'bloodGlucose'
  | 'co2Level'
  | 'bmi'
  | 'painScale'
  | 'fvc'
  | 'fev1'
  | 'fev1FvcRatio';

export type VitalPoint = {
  visitId: number;
  /** The instant the measurement belongs to: the visit's start. */
  at: string;
  value: number;
  caregivers: string[];
};

export type VitalSeries = {
  key: VitalKey;
  labelKey: string;
  unit?: string;
  points: VitalPoint[];
};

/**
 * Vitals in the order the visit form asks for them, sharing its label keys and units.
 *
 * Deliberately no target and no good/bad direction. Neither exists in the data: a
 * reference range is clinical judgement that varies by client — 94% saturation is a
 * good day for a COPD client and a bad one for someone else — and colouring a
 * sparkline red against a number nobody set for this client would be the card
 * asserting something no clinician said.
 */
export const CARD_VITALS: Array<{ key: VitalKey; labelKey: string; unit?: string }> = [
  { key: 'bloodPressureSystolic', labelKey: 'visits-vital-bp-systolic', unit: 'mmHg' },
  { key: 'bloodPressureDiastolic', labelKey: 'visits-vital-bp-diastolic', unit: 'mmHg' },
  { key: 'heartRate', labelKey: 'visits-vital-heart-rate', unit: 'bpm' },
  { key: 'oxygenSaturation', labelKey: 'visits-vital-oxygen-saturation', unit: '%' },
  { key: 'respiratoryRate', labelKey: 'visits-vital-respiratory-rate', unit: 'breaths/min' },
  { key: 'temperature', labelKey: 'visits-vital-temperature', unit: '°C' },
  { key: 'bloodGlucose', labelKey: 'visits-vital-blood-glucose', unit: 'mg/dL' },
  { key: 'co2Level', labelKey: 'visits-vital-co2-level', unit: 'mmHg' },
  { key: 'bmi', labelKey: 'visits-vital-bmi' },
  { key: 'painScale', labelKey: 'visits-vital-pain-scale' },
  { key: 'fvc', labelKey: 'visits-vital-fvc', unit: 'L' },
  { key: 'fev1', labelKey: 'visits-vital-fev1', unit: 'L' },
  { key: 'fev1FvcRatio', labelKey: 'visits-vital-fev1-fvc-ratio', unit: '%' },
];

/**
 * One series per vital that was actually measured, oldest point first.
 *
 * A vital nobody measured gets no row at all rather than an empty one: the card
 * would otherwise show ten rows of "no measurements" for a client whose caregivers
 * record blood pressure and nothing else. Cancelled visits are skipped — a
 * measurement on a visit that did not happen is data entered and then withdrawn.
 */
export const buildVitalSeries = (visits: CardVisit[]): VitalSeries[] => {
  const chronological = [...visits]
    .filter((visit) => visit.status !== VisitStatusEnum.CANCELLED)
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

  return CARD_VITALS.map(({ key, labelKey, unit }) => {
    const points: VitalPoint[] = [];

    for (const visit of chronological) {
      const value = visit[key];
      if (typeof value !== 'number') continue;
      points.push({
        visitId: visit.id,
        at: visit.startTime,
        value,
        caregivers: visitCaregiverNames(visit),
      });
    }

    return { key, labelKey, unit, points };
  }).filter((series) => series.points.length > 0);
};

/** Latest minus first, or `null` when one measurement cannot describe a change. */
export const vitalDelta = (series: VitalSeries): number | null => {
  if (series.points.length < 2) return null;
  const first = series.points[0].value;
  const last = series.points[series.points.length - 1].value;
  return Math.round((last - first) * 10) / 10;
};

export const vitalLatest = (series: VitalSeries): VitalPoint | null =>
  series.points.length > 0 ? series.points[series.points.length - 1] : null;

/* ================= The folder ================= */

/** Where a file came from, which decides the download endpoint. */
export type FolderFileSource = { type: 'client'; clientId: number } | { type: 'visit'; visitId: number };

export type FolderFile = {
  attachment: CardAttachment;
  source: FolderFileSource;
  /** The instant to sort and date it by. */
  at: string;
  /** The bucket the type filters use. */
  bucket: FolderBucket;
};

/**
 * The folder's buckets are the shared attachment vocabulary: the six kinds, plus
 * the two content-type fallbacks for files nobody has classified.
 */
export type FolderBucket = AttachmentBucket;
export const FOLDER_BUCKET_ORDER = ATTACHMENT_BUCKET_ORDER;
export const FOLDER_BUCKET_LABEL_KEYS = ATTACHMENT_BUCKET_LABEL_KEYS;
export const folderBucketOf = attachmentBucketOf;

/**
 * Every file belonging to this client: the ones uploaded against the client record
 * and the ones attached to their visits, newest first.
 *
 * Both sources are needed and neither is enough. Office documents live on the
 * client; the photos caregivers take live on the visit, and those are most of what
 * the folder is for.
 */
export const buildFolder = (
  clientId: number,
  clientAttachments: CardAttachment[],
  visits: CardVisit[]
): FolderFile[] => {
  const fromClient: FolderFile[] = clientAttachments.map((attachment) => ({
    attachment,
    source: { type: 'client', clientId },
    at: attachment.uploadedAt,
    bucket: folderBucketOf(attachment),
  }));

  const fromVisits: FolderFile[] = visits.flatMap((visit) =>
    (visit.attachments ?? []).map((attachment) => ({
      attachment,
      source: { type: 'visit' as const, visitId: visit.id },
      at: attachment.uploadedAt,
      bucket: folderBucketOf(attachment),
    }))
  );

  return [...fromClient, ...fromVisits].sort((a, b) => dayjs(b.at).valueOf() - dayjs(a.at).valueOf());
};

/**
 * The buckets present in this folder with their counts, in display order.
 *
 * Only non-empty buckets, so the filter row shows what is there instead of six
 * chips reading zero — and so the kind chips appear on their own the day something
 * starts setting the column.
 */
export const folderBucketCounts = (files: FolderFile[]): Array<{ bucket: FolderBucket; count: number }> => {
  const counts = new Map<FolderBucket, number>();
  for (const file of files) {
    counts.set(file.bucket, (counts.get(file.bucket) ?? 0) + 1);
  }
  return FOLDER_BUCKET_ORDER.filter((bucket) => counts.has(bucket)).map((bucket) => ({
    bucket,
    count: counts.get(bucket) as number,
  }));
};

/* ================= Timeline ================= */

export type TimelineGroup = {
  /** `YYYY-MM`, so the label can be formatted in the reader's locale. */
  month: string;
  visits: CardVisit[];
};

/**
 * The visits grouped by the month they start in, newest month and newest visit
 * first. Grouping keys off the visit's own local month, which is what a reader
 * scanning for "August" means.
 */
export const groupVisitsByMonth = (visits: CardVisit[]): TimelineGroup[] => {
  const groups = new Map<string, CardVisit[]>();

  for (const visit of [...visits].sort((a, b) => dayjs(b.startTime).valueOf() - dayjs(a.startTime).valueOf())) {
    const month = dayjs(visit.startTime).format('YYYY-MM');
    groups.set(month, [...(groups.get(month) ?? []), visit]);
  }

  return [...groups.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([month, monthVisits]) => ({ month, visits: monthVisits }));
};

/* ================= Client facts ================= */

/** Whole years, or `null` when no date of birth is recorded. */
export const clientAge = (client: Pick<ClientPopulated, 'dateOfBirth'>, now: Date = new Date()): number | null =>
  client.dateOfBirth ? dayjs(now).diff(dayjs(client.dateOfBirth), 'year') : null;

/** Attending before referring before anything else, so the list opens with the one to call. */
const DOCTOR_TYPE_ORDER = ['ATTENDING', 'REFERRING'];

const doctorRank = (type: string | null | undefined): number => {
  const rank = DOCTOR_TYPE_ORDER.indexOf(type ?? '');
  return rank === -1 ? DOCTOR_TYPE_ORDER.length : rank;
};

/**
 * Every doctor the client currently has, attending first.
 *
 * Read from `client.doctors` rather than the `attendingDoctor` summary because only
 * the list carries the specialisation, and "Δρ. Λάμπρου" without it tells a
 * coordinator nothing they did not already know. A relationship whose `endDate` has
 * passed is skipped: that doctor is history, and naming them would send a caregiver
 * to the wrong phone number. One ending TODAY has not ended.
 *
 * The rows are relationships and not people — the table keeps history, so the same
 * doctor can appear twice — so they are collapsed by `doctorId`, keeping the
 * best-ranked spell. A card listing one person twice reads as two doctors.
 */
export const cardDoctors = (client: Pick<ClientPopulated, 'doctors'>, now: Date = new Date()) => {
  const active = (client.doctors ?? []).filter(
    (doctor) => !doctor.endDate || !dayjs(doctor.endDate).isBefore(dayjs(now), 'day')
  );

  const byDoctor = new Map<number, (typeof active)[number]>();
  for (const doctor of [...active].sort((a, b) => doctorRank(a.doctorType) - doctorRank(b.doctorType))) {
    if (!byDoctor.has(doctor.doctorId)) byDoctor.set(doctor.doctorId, doctor);
  }

  return [...byDoctor.values()];
};

/**
 * The contact to show as next of kin: the primary one, else the emergency one,
 * else the first there is. Returns `null` rather than an empty row when the client
 * has no contacts.
 */
export const kinContact = (client: Pick<ClientPopulated, 'contacts'>) => {
  const contacts = client.contacts ?? [];
  return (
    contacts.find((contact) => contact.contactType === 'PRIMARY') ??
    contacts.find((contact) => contact.contactType === 'EMERGENCY') ??
    contacts[0] ??
    null
  );
};
